/**
 * @file BillingSMSLogsTab.js
 * @description Tab component for displaying SMS usage (allocation history) with pagination.
 */

import { useState, useEffect, useCallback } from 'react';
import { getSmsLog } from '../../../services/clientService';
import Spinner from '../../ui/spinners/Spinner';
import NotFoundBox from '../../ui/no_data_indicators/NotFoundBox';
import styles from '../Billing.module.scss';
import PaginationComponent from '../../helpers/PaginationComponent';
import Toast from '../../ui/toasts/Toast';
import { getText } from '../../../i18n';

const ITEMS_PER_PAGE = 20;

const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
    }).format(date);
};

const formatAmount = amount => {
    if (amount === null || amount === undefined || amount === '') return '-';
    return `$${Number(amount).toFixed(2)}`;
};

const BillingSMSLogsTab = ({ clientId }) => {
    const [logs, setLogs] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [isLoading, setIsLoading] = useState(true);

    const loadSmsLogs = useCallback(async () => {
        if (!clientId) return;

        try {
            setIsLoading(true);
            const response = await getSmsLog(0, 1000, clientId);
            setLogs(response.logs || []);
        } catch (error) {
            console.error('Failed to fetch SMS logs', error);
            Toast.error(getText('msg_6a797afe3d16'));
        } finally {
            setIsLoading(false);
        }
    }, [clientId]);

    useEffect(() => {
        loadSmsLogs();
    }, [loadSmsLogs]);

    const totalPages = Math.ceil(logs.length / ITEMS_PER_PAGE);
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const pagedLogs = logs.slice(startIndex, startIndex + ITEMS_PER_PAGE);

    const handlePageChange = newPage => {
        setCurrentPage(newPage);
    };

    if (isLoading) {
        return <Spinner width={50} className={styles.loadingSpinner} />;
    }

    return (
        <div className="col-12">
            <div className="table-responsive-xxl w-100">
                <table className="table table-hover align-middle">
                    <thead className="custom-table-header table-light">
                        <tr>
                            <th className="ps-4">
                                {getText('msg_eb9a4bc1c0c1')}
                            </th>
                            <th>{getText('msg_3deb74565196')}</th>
                            <th className="text-end">
                                {getText('msg_1593cc41aa24')}
                            </th>
                            <th className="text-end pe-5">
                                {getText('msg_3f09cdbd7a6c')}
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {pagedLogs.map((log, index) => (
                            <tr key={`${log.allocationDate}-${index}`}>
                                <td className="ps-4 item-page-changed-by">
                                    <span className="fw-normal">
                                        {formatDate(log.allocationDate)}
                                    </span>
                                </td>
                                <td>{log.reason || '-'}</td>
                                <td className="item-page-changed-by text-end">
                                    {log.count?.toLocaleString() || '-'}
                                </td>
                                <td className="item-page-changed-by text-end pe-5">
                                    {formatAmount(log.allocationCost)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {logs.length === 0 && (
                    <NotFoundBox
                        title={getText('msg_21a65f8b35e9')}
                        description={getText('msg_2c15a9bb9060')}
                    />
                )}
            </div>

            {logs.length > 0 && totalPages > 1 && (
                <PaginationComponent
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                />
            )}
        </div>
    );
};

export default BillingSMSLogsTab;
